import { useState } from 'react';
import { css, cx } from '@emotion/css';
import { Colors } from '../styles/colors';
import { fieldStyle, labelStyle } from '../styles/text';
import { useController, useFormContext } from 'react-hook-form';
import { EyeIconOff, EyeIconOn } from '../assets/icons';
import InputMask from 'react-input-mask';
import { ControlHeight, Motion, Radius, Shadow } from '../styles/tokens';

interface ITextInputProps {
  placeHolder: string;
  name: string;
  type?: string;
  markAsRequired?: boolean;
  defaultValue?: any;
  className?: string;
  onChange?: (value: any) => void;
  autoFocus?: boolean;
}

export const TextInput = (props: ITextInputProps): JSX.Element => {
  const { control, watch } = useFormContext();
  const {
    placeHolder,
    name,
    type = 'text',
    markAsRequired = false,
    className,
    defaultValue,
    onChange = (e: any) => e,
    autoFocus = false,
  } = props;

  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name,
  });

  const userInput = watch(name);
  const [passwordIsVisible, setPasswordIsVisible] = useState<boolean>(false);
  const togglePasswordVisibility = () => {
    setPasswordIsVisible(!passwordIsVisible);
  };

  const inputId = `field-${name}`;

  return (
    <div className={cx(textInputStyle(userInput, error), className)}>
      {type !== 'date' ? (
        <>
          <input
            id={inputId}
            className={cx(
              inputStyle,
              type === 'password' && passwordInputStyle,
            )}
            type={passwordIsVisible ? 'text' : type}
            {...field}
            value={field.value || ''}
            onChange={(e: any) => {
              field.onChange(e.target.value);
              onChange(e.target.value);
            }}
            defaultValue={defaultValue}
            aria-invalid={error ? true : undefined}
            autoFocus={autoFocus}
          />
          <label htmlFor={inputId} className={cx(fieldStyle, inputLabelStyle)}>
            {placeHolder}
            {markAsRequired && (
              <span className={requiredMarkStyle} aria-hidden="true">
                *
              </span>
            )}
          </label>
          {type === 'password' && (
            <button
              type="button"
              className={eyeButtonStyle}
              onClick={togglePasswordVisibility}
              aria-label={passwordIsVisible ? 'Hide password' : 'Show password'}
              aria-pressed={passwordIsVisible}
            >
              {passwordIsVisible ? <EyeIconOn /> : <EyeIconOff />}
            </button>
          )}
          {error && (
            <span className={cx(labelStyle, errorStyle)} role="alert">
              {error.message}
            </span>
          )}
        </>
      ) : (
        <>
          <InputMask
            id={inputId}
            className={inputStyle}
            {...field}
            value={field.value || ''}
            onChange={(e) => {
              field.onChange(e.target.value);
              onChange(e.target.value);
            }}
            defaultValue={defaultValue}
            mask={'99/99/9999'}
          />
          <label htmlFor={inputId} className={cx(fieldStyle, inputLabelStyle)}>
            {placeHolder}
            {markAsRequired && (
              <span className={requiredMarkStyle} aria-hidden="true">
                *
              </span>
            )}
          </label>
        </>
      )}
    </div>
  );
};

const textInputStyle = (userInput: any, error: any) => css`
  width: 100%;
  height: 5.25rem;
  position: relative;
  display: flex;
  flex-direction: column;

  &:focus-within label {
    ${labelStyle}
    transform: translate(1.125rem, 0.75rem) scale(1);
    color: ${Colors.CAPRI_BLUE};
  }
  label {
    ${userInput &&
    css`
      ${labelStyle}
      transform: translate(1.125rem, 0.75rem) scale(1);
      color: ${Colors.TEXT_SECONDARY};
    `}
  }
  input {
    ${error &&
    css`
      border-color: ${Colors.ERROR_DARK};
    `}
  }
  &:focus-within input {
    border-color: ${Colors.CAPRI_BLUE};
    box-shadow: ${Shadow.focus};
  }
`;

const inputLabelStyle = css`
  color: ${Colors.TEXT_SECONDARY};
  position: absolute;
  pointer-events: none;
  transform: translate(1.125rem, 1.25rem) scale(1);
  transform-origin: top left;
  transition:
    transform 200ms ease-out 0ms,
    color 200ms ease-out 0ms,
    font-size 200ms ease-out 0ms;
  text-transform: capitalize;
`;

const inputStyle = css`
  height: ${ControlHeight.input};
  border: 0.125rem solid ${Colors.BORDER_STRONG};
  border-radius: ${Radius.md};
  padding: 28px 16px 12px 16px;

  position: absolute;
  background: ${Colors.WHITE};
  outline: none;
  width: 100%;
  -webkit-appearance: none;
  -moz-appearance: textfield;
  transition:
    border-color ${Motion.fast},
    box-shadow ${Motion.fast};

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  @media (hover: hover) {
    &:hover:not(:focus) {
      border-color: ${Colors.WINDOW_GRAY};
    }
  }
`;

const passwordInputStyle = css`
  padding-right: 3.25rem;
`;

const requiredMarkStyle = css`
  color: ${Colors.ERROR_DARK};
  margin-left: 0.125rem;
`;

const errorStyle = css`
  color: ${Colors.ERROR_DARK};
  transform: translate(1.125rem, 62px) scale(1);
  transform-origin: top left;
  pointer-events: none;
  &:first-letter {
    text-transform: capitalize;
  }
`;

const eyeButtonStyle = css`
  appearance: none;
  background: none;
  border: 0;
  position: absolute;
  top: 0;
  right: 0;
  width: 3.5rem;
  height: ${ControlHeight.input};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${Colors.TEXT_SECONDARY};
  border-radius: ${Radius.md};

  > svg {
    display: block;
  }
`;
